import { useEffect, useMemo, useRef } from 'react';
import type { FieldPath, FieldValues, PathValue } from 'react-hook-form';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box } from '@mui/material';
import RhfSelect, {
  type RhfSelectProps,
} from '@core/components/forms/fields/RhfSelect';
import { getEntityById } from '@core/utils/collection';
import type { Color } from '../../../api/color';
import type { Fabric } from '../../../api/fabric';
import { ColorSwatch } from '../../components/ColorSwatch';
import FabricDialog from '../../components/FabricDialog';
import { useAppContext } from '../../context/AppContext';
import {
  getFabricColorOptions,
  isColorLinkedToFabric,
} from '../../utils/fabric';

type RhfColorSelectProps<T extends FieldValues> = {
  name: FieldPath<T>;
  label: string;
  fabricFieldName?: FieldPath<T>;
} & Omit<
  RhfSelectProps<T, Color, Fabric>,
  | 'name'
  | 'label'
  | 'items'
  | 'getOptionValue'
  | 'getOptionLabel'
  | 'renderOption'
  | 'renderSelectionAdornment'
  | 'options'
  | 'actionOption'
>;

export function RhfColorSelect<T extends FieldValues>({
  name,
  label,
  fabricFieldName,
  disabled,
  ...rest
}: RhfColorSelectProps<T>) {
  const { colors, fabrics, refreshContext } = useAppContext();
  const { control, getValues, setValue } = useFormContext<T>();
  const inputRef = useRef<HTMLInputElement>(null);
  const prevFabricIdRef = useRef<string | undefined>(undefined);

  const colorId = useWatch({ control, name }) as string | undefined;
  const fabricId = useWatch({
    control,
    name: (fabricFieldName ?? name) as FieldPath<T>,
    disabled: !fabricFieldName,
  }) as string | undefined;

  const fabric = fabricFieldName
    ? getEntityById(fabrics, String(fabricId ?? ''))
    : undefined;

  const items = useMemo(() => {
    if (!fabricFieldName) {
      return colors;
    }
    return getFabricColorOptions(fabric, colors, colorId);
  }, [colorId, colors, fabric, fabricFieldName]);

  useEffect(() => {
    if (!fabricFieldName) {
      return;
    }
    const currentFabricId = fabricId ? String(fabricId) : '';
    const prevFabricId = prevFabricIdRef.current;
    prevFabricIdRef.current = currentFabricId;
    if (
      prevFabricId !== undefined &&
      prevFabricId !== currentFabricId &&
      currentFabricId
    ) {
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [fabricFieldName, fabricId]);

  useEffect(() => {
    if (!fabricFieldName) {
      return;
    }

    const currentColorId = String(getValues(name) ?? '');
    if (!fabricId) {
      if (currentColorId) {
        setValue(name, '' as PathValue<T, typeof name>);
      }
      return;
    }

    if (currentColorId && !isColorLinkedToFabric(fabric, currentColorId)) {
      setValue(name, '' as PathValue<T, typeof name>);
    }
  }, [fabric, fabricFieldName, fabricId, getValues, name, setValue]);

  const isWaitingForFabric = fabricFieldName && !fabricId;

  return (
    <RhfSelect<T, Color, Fabric>
      name={name}
      label={label}
      items={items}
      getOptionValue={(color) => String(color.id)}
      getOptionLabel={(color) => color.name}
      renderOption={(option) => (
        <ColorSwatch hexCode={option.item.hexCode}>
          {option.item.name}
        </ColorSwatch>
      )}
      renderSelectionAdornment={(option) => (
        <Box sx={{ ml: 0.5 }}>
          <ColorSwatch hexCode={option.item.hexCode} />
        </Box>
      )}
      disabled={disabled ?? isWaitingForFabric}
      openOnFocus={!!fabricFieldName}
      inputRef={fabricFieldName ? inputRef : undefined}
      actionOption={
        fabric
          ? {
              label: 'Add new color…',
              renderDialog: (props) => (
                <FabricDialog {...props} fabric={fabric} />
              ),
              onComplete: async () => {
                await refreshContext();
              },
            }
          : undefined
      }
      {...rest}
    />
  );
}
